import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdInvestigationsByInvestigationIdActiveSessionOptions, getOrgAppearencesByAppearanceIdOptions, getOrgInvestigationsByIdOptions } from '@onefootprint/fetch-dashboard';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { usePlaybookBuilderStore } from '@/components/playbook-builder/hooks/use-store';
import useAgent from '@/hooks/use-agent';
import type { ResourceContext } from '../types';
const usePlaybookRouteResource = (pathname, id) => {
  const $ = _c(4);
  const storePlaybookId = usePlaybookBuilderStore(_temp);
  const storeName = usePlaybookBuilderStore(_temp2);
  const storeEditSessionId = usePlaybookBuilderStore(_temp3);
  if (!pathname.startsWith("/playbooks/[id]") || !id) {
    return;
  }
  const isLoaded = storePlaybookId === id;
  const t0 = isLoaded ? storeName || null : null;
  const t1 = isLoaded ? storeEditSessionId ?? null : null;
  let t2;
  if ($[0] !== id || $[1] !== t0 || $[2] !== t1) {
    t2 = {
      kind: "playbook",
      id,
      name: t0,
      edit_session_id: t1,
      version_id: null,
      selection: null,
      update_description: null
    };
    $[0] = id;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const APPEARANCE_STUDIO_ROUTE = '/developers/appearance/[id]';
const useAppearanceRouteResource = (pathname, id, enabled) => {
  const $ = _c(10);
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname.startsWith(APPEARANCE_STUDIO_ROUTE);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isStudioRoute = t0;
  const t1 = id ?? "";
  let t2;
  if ($[2] !== t1) {
    t2 = getOrgAppearencesByAppearanceIdOptions({
      path: {
        appearanceId: t1
      }
    });
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = enabled && isStudioRoute && !!id;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data
  } = useQuery(t4);
  if (!isStudioRoute || !id) {
    return;
  }
  const name = data?.name;
  const t5 = name ?? null;
  let t6;
  if ($[7] !== id || $[8] !== t5) {
    t6 = {
      kind: "appearance",
      id,
      name: t5,
      logo_url: null,
      edit: null,
      proposal: null
    };
    $[7] = id;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
const ENTITY_ROUTES = [['/users/[id]', 'person'], ['/businesses/[id]', 'business']] as const;

/** The entity (or the onboarding within it) a `/users` or `/businesses` route points at. */
const entityRouteResource = (pathname: string, id: string | undefined, onboardingId: string | undefined): ResourceContext | undefined => {
  if (!id) return undefined;
  for (const [prefix, entityKind] of ENTITY_ROUTES) {
    if (!pathname.startsWith(prefix)) continue;
    if (onboardingId && pathname.startsWith(`${prefix}/onboardings/[onboarding_id]`)) {
      return {
        kind: 'onboarding',
        id: onboardingId,
        fp_id: id,
        entity_kind: entityKind
      };
    }
    // Entity names are sensitive vault data and not loaded here; the dashboard shows the fp_id.
    return {
      kind: 'entity',
      id,
      entity_kind: entityKind,
      name: null
    };
  }
  return undefined;
};

// The resource a chat is about, derived from the current route. The backend appends a line
// naming it to each user turn so Percy knows what's being worked on.
//
// `enabled` follows `useChatWidgetEnabled`: no composer, no resource, and none of the queries.
const useResourceContext = t0 => {
  const $ = _c(18);
  const enabled = t0 === undefined ? true : t0;
  const router = useRouter();
  let t1;
  if ($[0] !== router.query) {
    t1 = name => {
      const value = router.query[name];
      return typeof value === "string" && value ? value : undefined;
    };
    $[0] = router.query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const param = t1;
  const {
    pathname
  } = router;
  let t2;
  if ($[2] !== param) {
    t2 = param("id");
    $[2] = param;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const id = t2;
  const investigationId = param("investigationId");
  const isInvestigationRoute = pathname.startsWith("/investigations/[investigationId]");
  const isAgentRoute = pathname.startsWith("/agents/[id]");
  const {
    data: agent
  } = useAgent(enabled && isAgentRoute ? id : undefined);
  const {
    data: investigation
  } = useQuery({
    ...getOrgInvestigationsByIdOptions({
      path: {
        id: investigationId ?? ""
      }
    }),
    enabled: enabled && isInvestigationRoute && !!investigationId
  });
  const investigationFpId = investigation?.fpId;
  const t3 = getEntitiesByFpIdInvestigationsByInvestigationIdActiveSessionOptions({
    path: {
      fpId: investigationFpId ?? "",
      investigationId: investigationId ?? ""
    }
  });
  const t4 = enabled && isInvestigationRoute && !!investigationId && !!investigationFpId;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = {
      ...t3,
      enabled: t4
    };
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: activeSession
  } = useQuery(t5);
  const priorChatSessionId = activeSession?.kind === "active" ? activeSession.sessionId : activeSession?.kind === "no_active_session" ? activeSession.chatSessionId ?? null : null;
  const playbookResource = usePlaybookRouteResource(pathname, id);
  const appearanceResource = useAppearanceRouteResource(pathname, id, enabled);
  if (!enabled) {
    return;
  }
  if (isAgentRoute && id) {
    const t6 = agent?.name ?? null;
    let t7;
    if ($[7] !== id || $[8] !== t6) {
      t7 = {
        kind: "agent",
        id,
        name: t6,
        selection: null
      };
      $[7] = id;
      $[8] = t6;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  if (playbookResource) {
    return playbookResource;
  }
  if (appearanceResource) {
    return appearanceResource;
  }
  if (isInvestigationRoute && investigationId) {
    const t6 = investigation?.title ?? null;
    let t7;
    if ($[10] !== investigationId || $[11] !== priorChatSessionId || $[12] !== t6) {
      t7 = {
        kind: "investigation",
        id: investigationId,
        name: t6,
        fp_id: null,
        agent_execution_id: null,
        chat_session_id: priorChatSessionId
      };
      $[10] = investigationId;
      $[11] = priorChatSessionId;
      $[12] = t6;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    return t7;
  }
  let t6;
  if ($[14] !== id || $[15] !== param || $[16] !== pathname) {
    t6 = entityRouteResource(pathname, id, param("onboarding_id"));
    $[14] = id;
    $[15] = param;
    $[16] = pathname;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export default useResourceContext;
function _temp(s) {
  return s.data.playbookId;
}
function _temp2(s_0) {
  return s_0.data.settings.name;
}
function _temp3(s_1) {
  return s_1.data.sessionId;
}